/**
 * Upload rules for the document manager (CR-7460).
 *
 * The picker and the client-side check both come from
 * DOCUMENT_MANAGER_EXTENSIONS — the frontend copy of
 * documents.Document.ALLOWED_EXTENSIONS — so neither offers a type (SVG, say)
 * that the server then refuses.
 */
import {
  acceptAttr,
  DOCUMENT_MANAGER_EXTENSIONS,
  fileTypeRefusedMessage,
  hasAllowedExtension,
} from '../../lib/fileTypes';

/** `accept` attribute for the upload dialog's picker. */
export const DOCUMENT_ACCEPT = acceptAttr(DOCUMENT_MANAGER_EXTENSIONS);

/** The "Allowed types" line under the drop zone: the same list the picker offers. */
export const DOCUMENT_TYPES_HINT = DOCUMENT_MANAGER_EXTENSIONS
  .map((ext) => ext.toUpperCase())
  .join(', ');

/** Maximum file size (50MB - must match server-side documents/services.py). */
export const MAX_DOCUMENT_FILE_SIZE = 50 * 1024 * 1024;

/**
 * Maximum number of files in a single multi-file upload. Keeps a stray
 * "select 500 files" from firing hundreds of sequential POST requests.
 */
export const MAX_FILES_PER_UPLOAD = 20;

/**
 * Check one file against the document manager's rules.
 *
 * @param file - The picked or dropped file
 * @returns The refusal message, or null when the server would accept it
 */
export function validateDocumentFile(file: File): string | null {
  if (!hasAllowedExtension(file.name, DOCUMENT_MANAGER_EXTENSIONS)) {
    return fileTypeRefusedMessage(DOCUMENT_MANAGER_EXTENSIONS);
  }
  if (file.size > MAX_DOCUMENT_FILE_SIZE) {
    return `File size exceeds maximum allowed size of ${MAX_DOCUMENT_FILE_SIZE / 1024 / 1024}MB.`;
  }
  return null;
}
